import { Inject, Injectable } from '@angular/core';
import { Observable, of, ReplaySubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Navigation } from './navigation.types';
import { NAVIGATION_CONFIG } from '../config';
import { TranslocoService } from '@ngneat/transloco';
import { FuseNavigationItem } from '@fuse/components/navigation';

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  /**
   * Constructor
   */
  constructor(
    @Inject(NAVIGATION_CONFIG) private _navigationConfig: FuseNavigationItem[],
    private _translate: TranslocoService,
  ) {
    this._updateTranslations(_navigationConfig);
    _translate.langChanges$.subscribe(() =>
      setTimeout(() => this._updateTranslations(_navigationConfig), 0),
    );
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for navigation
   */
  public get navigation$(): Observable<Navigation> {
    return this._navigation.asObservable();
  }

  private _navigation: ReplaySubject<Navigation> = new ReplaySubject<Navigation>(1);

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get all navigation data
   */
  get(): Observable<Navigation> {
    return of({
      classic: this._navigationConfig,
      default: this._navigationConfig,
      compact: this._navigationConfig,
      futuristic: this._navigationConfig,
      horizontal: this._navigationConfig,
    }).pipe(
      tap((navigation) => {
        this._navigation.next(navigation);
      }),
    );
  }

  private _updateTranslations(navigationConfig: FuseNavigationItem[]): void {
    const translatedConfig = this._translateNavItems(navigationConfig);

    this._navigation.next({
      classic: translatedConfig,
      default: translatedConfig,
      compact: translatedConfig,
      futuristic: translatedConfig,
      horizontal: translatedConfig,
    });
  }

  private _translateNavItems(items: FuseNavigationItem[]): FuseNavigationItem[] {
    return items.map((item) => ({
      ...item,
      title: item.title ? this._translate.translate(item.title) : item.title,
      children: item.children && this._translateNavItems(item.children),
    }));
  }
}
